import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { AXIOS_INSTANCE, customInstance } from '../../services/api-client'
import type {
  RevenueRedshiftConnectorOption,
  RevenueRedshiftDataSourceConfig,
  RevenueRedshiftDataSourceUpdate,
} from '../revenue-close/redshiftDataSourceTypes'
import type { AgentReview, SubledgerHealth, SubledgerRun } from './types'

const BASE = '/api/subledger-revenue-reconciler'

export const subledgerKeys = {
  all: ['subledger-revenue-reconciler'] as const,
  run: () => [...subledgerKeys.all, 'run'] as const,
  health: () => [...subledgerKeys.all, 'health'] as const,
  config: () => [...subledgerKeys.all, 'config'] as const,
  connectors: () => [...subledgerKeys.all, 'connectors'] as const,
  agentReview: () => [...subledgerKeys.all, 'agent-review'] as const,
}

export function useSubledgerRun(options?: { enabled?: boolean }) {
  return useQuery<SubledgerRun>({
    queryKey: subledgerKeys.run(),
    queryFn: () => customInstance({ url: `${BASE}/run`, method: 'GET' }),
    staleTime: 60_000,
    enabled: options?.enabled ?? true,
  })
}

export function useSubledgerHealth() {
  return useQuery<SubledgerHealth>({
    queryKey: subledgerKeys.health(),
    queryFn: () => customInstance({ url: `${BASE}/health`, method: 'GET' }),
    staleTime: 60_000,
  })
}

export function useSubledgerDataSourceConfig() {
  return useQuery<RevenueRedshiftDataSourceConfig>({
    queryKey: subledgerKeys.config(),
    queryFn: () => customInstance({ url: `${BASE}/config/data-source`, method: 'GET' }),
    staleTime: 60_000,
  })
}

export function useSubledgerAvailableConnectors() {
  return useQuery<RevenueRedshiftConnectorOption[]>({
    queryKey: subledgerKeys.connectors(),
    queryFn: () => customInstance({ url: `${BASE}/config/available-connectors`, method: 'GET' }),
    staleTime: 60_000,
  })
}

export function useSetSubledgerDataSource() {
  const queryClient = useQueryClient()
  return useMutation<RevenueRedshiftDataSourceConfig, Error, RevenueRedshiftDataSourceUpdate>({
    mutationFn: payload =>
      customInstance({
        url: `${BASE}/config/data-source`,
        method: 'PUT',
        data: payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subledgerKeys.all })
    },
  })
}

export function useClearSubledgerDataSource() {
  const queryClient = useQueryClient()
  return useMutation<RevenueRedshiftDataSourceConfig, Error, void>({
    mutationFn: () =>
      customInstance({
        url: `${BASE}/config/data-source`,
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subledgerKeys.all })
    },
  })
}

export function useTestSubledgerDataSource() {
  return useMutation<SubledgerHealth, Error, RevenueRedshiftDataSourceUpdate>({
    mutationFn: payload =>
      customInstance({
        url: `${BASE}/config/test-connection`,
        method: 'POST',
        data: payload,
      }),
  })
}

export function useSubledgerAgentReview() {
  return useMutation<AgentReview, Error, { use_llm?: boolean }>({
    mutationKey: subledgerKeys.agentReview(),
    mutationFn: body =>
      customInstance({
        url: `${BASE}/agent/review`,
        method: 'POST',
        data: { use_llm: body.use_llm ?? false },
      }),
  })
}

export async function downloadSubledgerExport(
  exportType: 'tieout' | 'reconciling_items' | 'journal' | 'audit',
) {
  const response = await AXIOS_INSTANCE.get(`${BASE}/exports/${exportType}`, {
    responseType: 'blob',
  })
  const disposition = response.headers['content-disposition'] || ''
  const match = disposition.match(/filename="([^"]+)"/)
  const filename =
    match?.[1] || {
      tieout: 'OUTPUT_EC03e_SubLedger_GL_TieOut_Apr2025.csv',
      reconciling_items: 'OUTPUT_EC03e_Reconciling_Items_Apr2025.csv',
      journal: 'OUTPUT_EC03e_NetSuite_JE_Import_Apr2025.csv',
      audit: 'OUTPUT_EC03e_Audit_Trail_Apr2025.csv',
    }[exportType]
  const url = window.URL.createObjectURL(response.data)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}
